// Side-panel menu for the top-level Policies row (operator request, 23
// Oct): the same pattern as Insights → Dashboards. "All policies" opens
// the hub; under category headings, each rule set the org has is a
// shortcut straight to the page where it's edited, with a short count.
//
// Rows come from the hub's own inventory (GET /policy-inventory), so the
// menu applies exactly the hub's gates: only rule sets the org has, only
// for admins (the inventory is admin-only). Locked and not-yet-available
// rule sets stay on the hub, where they're explained. AppLayout remounts
// on every page, so the inventory is cached briefly per user and org.

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import type { AppSidebarItem } from './apps';
import { getPolicyInventory } from '../pages/policiesHub/api';
import type { PolicyInventory } from '../pages/policiesHub/catalog';
import { ALL_POLICIES, policyMenuItems } from './policyMenu';

const TTL_MS = 60_000;
const cache = new Map<string, { at: number; inventory: PolicyInventory }>();

/** Children for the Policies row: generated rows once the inventory
 *  loads; just "All policies" for non-admins or if it can't be read. */
export function usePolicyMenu(): AppSidebarItem[] {
  const { session } = useAuth();
  const orgId = session?.activeOrgId ?? null;
  const uid = session?.user.uid ?? null;
  const isAdmin = !!session?.orgRoles && (session.orgRoles.includes('owner') || session.orgRoles.includes('admin'));
  const cacheKey = uid && orgId ? `${uid}:${orgId}` : null;
  const [inventory, setInventory] = useState<PolicyInventory | null>(() =>
    cacheKey ? cache.get(cacheKey)?.inventory ?? null : null,
  );

  useEffect(() => {
    if (!session || !orgId || !cacheKey || !isAdmin) return;
    const hit = cache.get(cacheKey);
    if (hit && Date.now() - hit.at < TTL_MS) {
      setInventory(hit.inventory);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const inv = await getPolicyInventory(await session.user.getIdToken(), orgId);
        cache.set(cacheKey, { at: Date.now(), inventory: inv });
        if (!cancelled) setInventory(inv);
      } catch {
        // The menu falls back to "All policies"; the hub shows the error.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, orgId, cacheKey, isAdmin]);

  return useMemo(() => (isAdmin && inventory ? policyMenuItems(inventory) : [ALL_POLICIES]), [isAdmin, inventory]);
}
